/**
 * The expanded row: one entry's documents, rendered for what they actually are.
 *
 * The server classifies each entry's document pair (`payload_shape`), and this
 * component renders exactly that shape -- never inferring one from nulls. The
 * case that matters most is a DENIED or FAILED attempt: its `after_value` is
 * the payload the caller TRIED to send, so it is labelled as an attempt and
 * never shown in an "After" pane, which would tell an operator that a refused
 * request changed something (BR-F1.5-13).
 */

import { AlertTriangle, CircleSlash, FileWarning, Info, RotateCw } from 'lucide-react';

import type { AuditLogEntryDetail } from '@/api/client';
import { useAuditLogEntryQuery } from '@/api/queries';
import { ABSENT_FIELD } from '@/ui/emptyValue';

import { formatTimestamp, zoneLabelFor, type TimeZoneMode } from './auditTime';

import {
  contextEntries,
  diffDocuments,
  fieldsOfDocument,
  formatBytes,
  isRedacted,
  NON_REQUEST_SENTINEL,
  renderValue,
  truncationSizes,
  type EntityDiff,
  type Json,
} from './payloadView';

function Value({ value, present = true }: { value: Json; present?: boolean }) {
  if (!present) return <span className="diff-absent">{ABSENT_FIELD}</span>;
  if (isRedacted(value)) return <span className="diff-redacted">[REDACTED]</span>;
  return <>{renderValue(value)}</>;
}

type Side = 'before' | 'after';

function DiffPane({
  side,
  title,
  blocks,
}: {
  side: Side;
  title: string;
  blocks: EntityDiff[];
}) {
  return (
    <div className="diff-pane">
      <div className={`diff-head ${side}`}>{title}</div>
      <div className="diff-body">
        {blocks.map((block) => (
          <div key={block.entityKey || '(document)'} className="diff-entity">
            {block.entityKey && <div className="diff-entity-key">{block.entityKey}</div>}
            {block.fields.map((row) => {
              const cell = row[side];
              return (
                <div key={row.field}>
                  <span className="k">{row.field}:</span>{' '}
                  <span className={row.changed && cell.present ? 'changed' : undefined}>
                    <Value value={cell.value} present={cell.present} />
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function Note({
  icon,
  tone = 'neutral',
  children,
}: {
  icon: React.ReactNode;
  tone?: 'neutral' | 'warning';
  children: React.ReactNode;
}) {
  return (
    <div className={`detail-note detail-note-${tone}`}>
      {icon}
      <div>{children}</div>
    </div>
  );
}

function PayloadView({ entry }: { entry: AuditLogEntryDetail }) {
  const { before_value: before, after_value: after } = entry;

  switch (entry.payload_shape) {
    case 'change_set': {
      const blocks = diffDocuments(before, after);
      return (
        <div className="kv-diff">
          <DiffPane side="before" title="Before" blocks={blocks} />
          <DiffPane side="after" title="After" blocks={blocks} />
        </div>
      );
    }

    case 'created':
      return (
        <div className="kv-diff single">
          <DiffPane side="after" title="Created" blocks={diffDocuments(null, after)} />
        </div>
      );

    case 'removed':
      return (
        <div className="kv-diff single">
          <DiffPane side="before" title="Removed" blocks={diffDocuments(before, null)} />
        </div>
      );

    case 'no_change':
      return (
        <Note icon={<CircleSlash aria-hidden="true" />}>
          The request completed but changed no fields.
        </Note>
      );

    case 'attempted_payload': {
      const fields = fieldsOfDocument(after);
      return (
        <div className="kv-diff single">
          <div className="diff-pane">
            <div className="diff-head attempted">
              Attempted payload: {entry.outcome === 'DENIED' ? 'refused' : 'not applied'}
            </div>
            <div className="diff-body">
              {fields.length === 0 ? (
                <div>
                  <span className="k">payload:</span> <Value value={after} />
                </div>
              ) : (
                fields.map(({ field, value }) => (
                  <div key={field}>
                    <span className="k">{field}:</span> <Value value={value} />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      );
    }

    case 'truncated': {
      const { originalBytes, capBytes } = truncationSizes(before, after);
      return (
        <Note icon={<FileWarning aria-hidden="true" />} tone="warning">
          The payload exceeded the audit size cap
          {originalBytes !== null && (
            <>
              {' '}
              (<span className="mono">{formatBytes(originalBytes)}</span> recorded
              {capBytes !== null && (
                <>
                  , cap <span className="mono">{formatBytes(capBytes)}</span>
                </>
              )}
              )
            </>
          )}{' '}
          and was replaced by a marker. The change itself was applied and is recorded by
          this entry.
        </Note>
      );
    }

    case 'none':
      // Q-f1.5-1: a sign-in, or a refused read with no request body. Nothing
      // was captured, so there is no pane to draw and no attempt to label.
      return <Note icon={<Info aria-hidden="true" />}>No data recorded</Note>;
  }
}

function MetaItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="detail-meta-item">
      <span className="hint">{label}</span>
      <span className="mono">{children}</span>
    </div>
  );
}

function Meta({ entry, timeZone }: { entry: AuditLogEntryDetail; timeZone: TimeZoneMode }) {
  const isJob = entry.http_method === NON_REQUEST_SENTINEL;
  return (
    <div className="detail-meta">
      {/* The same clock the list column is in, and named -- an expanded entry
          that disagreed with the row above it would be read as two events.
          The raw stored instant stays one hover away. */}
      <MetaItem label={`Occurred (${zoneLabelFor(timeZone)})`}>
        <span title={entry.occurred_at}>{formatTimestamp(entry.occurred_at, timeZone)}</span>
      </MetaItem>
      <MetaItem label={isJob ? 'Origin' : 'Route'}>
        {isJob ? 'background job' : `${entry.http_method} ${entry.route_template}`}
      </MetaItem>
      {entry.request_id && <MetaItem label="Request">{entry.request_id}</MetaItem>}
      {entry.source_ip && <MetaItem label="Source">{entry.source_ip}</MetaItem>}
      {entry.actor_user_id && <MetaItem label="Actor id">{entry.actor_user_id}</MetaItem>}
      {entry.on_behalf_of_user_id && (
        <MetaItem label="On behalf of">{entry.on_behalf_of_user_id}</MetaItem>
      )}
      {entry.entity_id && <MetaItem label="Entity id">{entry.entity_id}</MetaItem>}
      {entry.user_agent && <MetaItem label="Agent">{entry.user_agent}</MetaItem>}
      {contextEntries(entry.context).map(({ key, value }) => (
        <MetaItem key={key} label={key}>
          {value}
        </MetaItem>
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="kv-diff detail-skeleton" aria-hidden="true">
      {[0, 1].map((pane) => (
        <div key={pane} className="diff-pane">
          <div className="diff-head">
            <span className="skeleton-bar" />
          </div>
          <div className="diff-body">
            {[0, 1, 2].map((line) => (
              <div key={line}>
                <span className="skeleton-bar" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AuditEntryDetail({
  entryId,
  timeZone,
}: {
  entryId: string;
  timeZone: TimeZoneMode;
}) {
  const { data, isPending, isError, error, refetch, isFetching } = useAuditLogEntryQuery(entryId);

  if (isPending) {
    return (
      <div className="detail-panel" aria-busy="true">
        <DetailSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="detail-panel">
        <Note icon={<AlertTriangle aria-hidden="true" />} tone="warning">
          This entry could not be loaded. {error instanceof Error ? error.message : ''}{' '}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RotateCw aria-hidden="true" /> Retry
          </button>
        </Note>
      </div>
    );
  }

  return (
    <div className="detail-panel">
      {data.reason && data.outcome !== 'SUCCESS' && (
        <div className="detail-reason">
          <span className={`badge ${data.outcome === 'DENIED' ? 'pill-warning' : 'pill-danger'}`}>
            {data.outcome === 'DENIED' ? 'Denied' : 'Failed'}
          </span>
          <span className="hint">Reason</span>
          <span className="mono">{data.reason}</span>
        </div>
      )}
      <PayloadView entry={data} />
      <Meta entry={data} timeZone={timeZone} />
    </div>
  );
}
